import { RestError } from "api/restClient.types";
import ExpandableNotification from "./ExpandableNotification";
import SimpleNotification from "./SimpleNotification";
import { describeRestError } from "./restErrorMessage";

type RestErrorNotificationProps = {
    titleOverride?: string;
    error: RestError;
    alwaysExpanded?: boolean;
};

export default ({ titleOverride, error, alwaysExpanded }: RestErrorNotificationProps) => {
    const { status, statusText } = error?.response ?? {};
    const description = describeRestError(error, titleOverride);

    // Gateway/proxy error (raw HTML body) or network failure: show a short friendly
    // line, keeping the raw body (if any) behind the existing expand/copy affordance
    // instead of dumping it into the toast.
    if (description.kind === "friendly") {
        if (description.rawDetails) {
            return (
                <ExpandableNotification
                    title={description.title}
                    subtitle={description.message}
                    details={description.rawDetails}
                    detailsForClipboard={description.rawDetails}
                    alwaysExpanded={alwaysExpanded}
                />
            );
        }
        return <SimpleNotification title={description.title} subtitle={description.message} />;
    }

    // Our normal structured JSON error body: unchanged behavior.
    const { title, message } = description;
    const effectiveTitle = titleOverride ? `${titleOverride}${title ? `: ${title}` : ""}` : title;

    if (effectiveTitle && message) {
        return (
            <ExpandableNotification
                title={effectiveTitle}
                details={message}
                alwaysExpanded={alwaysExpanded}
            />
        );
    }

    if (effectiveTitle) {
        return <SimpleNotification title={effectiveTitle} subtitle={`${error}`} />;
    }

    if (statusText) {
        return <SimpleNotification title={statusText} subtitle={`${status}`} />;
    }

    return <SimpleNotification title={`${error}`} />;
};
